import React from 'react'

type LazyModule = { default: React.ComponentType }

type CacheEntry = {
  promise: Promise<LazyModule>
  result?: LazyModule
  error?: unknown
}

const cache = new WeakMap<() => Promise<LazyModule>, CacheEntry>()

/**
 * Drop-in for React.lazy that can paint synchronously after preload().
 * React.lazy still suspends one microtask after a cached import(), which
 * mismatches SSR onAllReady HTML (React #418).
 */
export function lazyWithPreload (
  factory: () => Promise<{ default: React.ComponentType }>,
): React.ComponentType & { preload: () => Promise<unknown> } {
  function getEntry (): CacheEntry {
    let entry = cache.get(factory)
    if (!entry) {
      let created: CacheEntry
      const promise = factory().then(
        (mod) => {
          created.result = mod
          return mod
        },
        (err) => {
          created.error = err
          throw err
        },
      )
      created = { promise }
      entry = created
      cache.set(factory, entry)
    }
    return entry
  }

  function Component (props: Record<string, unknown>) {
    const entry = getEntry()
    if (entry.error !== undefined) {
      throw entry.error
    }
    if (entry.result) {
      return React.createElement(entry.result.default, props)
    }
    throw entry.promise
  }

  Component.preload = function preload () {
    return getEntry().promise
  }

  return Component
}

export default lazyWithPreload
